import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { ArrowRight, Sparkles } from "lucide-react";

const APP_MOCKUP_URL = "/app-mockup.png";
const FLOATING_FOOD_URL = "/floating-food.png";

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="inicio"
      className="relative min-h-screen pt-24 pb-16 overflow-hidden hero-pattern"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[85vh]">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Overline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center gap-2"
            >
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-primary">
                La Nueva Era del Delivery
              </span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-foreground"
              data-testid="hero-heading"
            >
              El Sabor Que{" "}
              <span className="gradient-text">Mereces</span>, Al Instante.
            </motion.h1>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-lg"
              data-testid="hero-subheading"
            >
              Descubre todos tus restaurantes favoritos en un solo lugar. 
              Pide con un toque y recibe en minutos. ¡La comida que amas, 
              cuando la quieres!
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-wrap gap-4"
            >
              <Button
                onClick={() => scrollToSection("#descargar")}
                size="lg"
                className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-glow flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Descargar App
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => scrollToSection("#registro")}
                variant="outline"
                size="lg"
                className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-white rounded-full px-8 py-6 text-lg font-bold transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                Soy un Restaurante
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex gap-8 pt-4"
            >
              <div className="text-center" data-testid="stat-restaurants">
                <span className="text-3xl font-black gradient-text">100+</span>
                <p className="text-sm text-muted-foreground">Restaurantes</p>
              </div>
              <div className="text-center" data-testid="stat-delivery">
                <span className="text-3xl font-black gradient-text">15min</span>
                <p className="text-sm text-muted-foreground">Entrega Promedio</p>
              </div>
              <div className="text-center" data-testid="stat-users">
                <span className="text-3xl font-black gradient-text">5K+</span>
                <p className="text-sm text-muted-foreground">Usuarios Felices</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - App Mockup & Floating Elements */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative flex justify-center items-center"
          >
            {/* Floating Food Elements */}
            <motion.img
              src={FLOATING_FOOD_URL}
              alt="Delicious Food"
              className="absolute -top-10 -left-10 w-40 h-40 object-contain z-10"
              animate={{
                y: [0, -20, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              data-testid="floating-food-1"
            />

            {/* App Mockup */}
            <motion.div
              className="relative z-20"
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <img
                src={APP_MOCKUP_URL}
                alt="Apedir App"
                className="w-80 md:w-96 phone-mockup"
                data-testid="app-mockup"
              />
              
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full scale-75 -z-10" />
            </motion.div>

            {/* Background Decoration */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent/10 rounded-full blur-3xl -z-10" />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-muted-foreground/30 rounded-full flex justify-center pt-2"
        >
          <motion.div
            animate={{ y: [0, 8, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-primary rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
